/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import {ResponseData, showDialog} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {ProductReviewAnalyse} from '../../../../util/interfaces';
import {analyseProductReviews} from '../../../../util/requestSender';
import {createErrorMessage} from '../../../../util/utils';
import {AppContext} from '../../../misc/appContext';
import ProductReviewsAnalyse from './productReviewsAnalyse';

interface Props {
    ean: string;
    abortController: AbortController;
}

export type ReviewAnalysisHandle = {
    analyse: VoidFunction;
    setLoading: (value: boolean) => void;
}

function ProductReviewsAnalyseController(props: Props, ref: ForwardedRef<ReviewAnalysisHandle>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {ean, abortController} = props;
    const [loading, setLoading] = useState<Record<ProductReviewAnalyse, boolean>>({
        [ProductReviewAnalyse.POSITIVE]: false,
        [ProductReviewAnalyse.NEGATIVE]: false,
        [ProductReviewAnalyse.IMPROVEMENTS]: false,
        [ProductReviewAnalyse.TARGET]: false,
        [ProductReviewAnalyse.CUSTOMERS]: false
    });
    const [result, setResult] = useState<Record<ProductReviewAnalyse, string | null>>({
        [ProductReviewAnalyse.POSITIVE]: null,
        [ProductReviewAnalyse.NEGATIVE]: null,
        [ProductReviewAnalyse.IMPROVEMENTS]: null,
        [ProductReviewAnalyse.TARGET]: null,
        [ProductReviewAnalyse.CUSTOMERS]: null
    });

    function setAllLoading(value: boolean) {
        setLoading({
            [ProductReviewAnalyse.POSITIVE]: value,
            [ProductReviewAnalyse.NEGATIVE]: value,
            [ProductReviewAnalyse.IMPROVEMENTS]: value,
            [ProductReviewAnalyse.TARGET]: value,
            [ProductReviewAnalyse.CUSTOMERS]: value
        });
    }

    function onAnalyse() {
        setAllLoading(true);
        Promise.all([
            onAnalyseRequest(ProductReviewAnalyse.POSITIVE),
            onAnalyseRequest(ProductReviewAnalyse.IMPROVEMENTS),
            onAnalyseRequest(ProductReviewAnalyse.TARGET),
            onAnalyseRequest(ProductReviewAnalyse.CUSTOMERS)
        ]).catch((err: string) => {
            showDialog(createErrorMessage(err), 'AI analyse mislukt');
        });
    }

    function onAnalyseRequest(analyseType: ProductReviewAnalyse) {
        return new Promise<void>((resolve, reject) => {
            if (!account) return reject();
            analyseProductReviews({
                abortController: abortController, data: {account_id: account.id, ean, analyse: analyseType},
                callback: (data: ResponseData) => {
                    if (analyseType === ProductReviewAnalyse.POSITIVE)
                        setLoading((current) => ({
                            ...current, [ProductReviewAnalyse.POSITIVE]: false, [ProductReviewAnalyse.NEGATIVE]: false
                        }));
                    else setLoading((current) => ({...current, [analyseType]: false}));
                    if (data.success) {
                        const aiResponse = data.response;
                        if (analyseType === ProductReviewAnalyse.POSITIVE) {
                            setResult((current) => ({
                                ...current, [ProductReviewAnalyse.POSITIVE]: aiResponse[0],
                                [ProductReviewAnalyse.NEGATIVE]: aiResponse[1]
                            }));
                        } else
                            setResult((current) => ({...current, [analyseType]: aiResponse}));
                        resolve();
                    } else reject(data.message);
                }
            });
        });
    }

    useImperativeHandle(ref, () => ({analyse: onAnalyse, setLoading: setAllLoading}));

    return (
        <ProductReviewsAnalyse onAnalyse={onAnalyse} loading={loading} result={result} />
    );
}

export default forwardRef<ReviewAnalysisHandle, Props>(ProductReviewsAnalyseController);